import type { DrivingLimitStatus } from '../../../api/apiService';
import type { DriverLimits, DriverWorkState, DriverWorkStateSnapshot } from './types';

// Example data for the ALL-136 design preview only. Not used by the app.

const H = 3600;
const M = 60;

export type LimitsScenario = 'ok' | 'warning' | 'critical' | 'reached' | 'empty';

export const LIMITS_SCENARIOS: LimitsScenario[] = ['ok', 'warning', 'critical', 'reached', 'empty'];
export const WORK_STATES: DriverWorkState[] = ['resting', 'available', 'driving'];

const iso = (ms: number) => new Date(ms).toISOString();

const rolling = (
  nowMs: number,
  driven: number,
  status: DrivingLimitStatus['status'],
  isDriving: boolean,
  belowAgainInSeconds: number | null = null,
): DrivingLimitStatus => {
  const limit = 10 * H;
  const remaining = Math.max(0, limit - driven);
  return {
    status,
    is_driving: isDriving,
    window_hours: 24,
    window_start: iso(nowMs - 24 * H * 1000),
    calculated_at: iso(nowMs),
    limit_seconds: limit,
    warning_lead_seconds: H,
    driven_seconds: driven,
    remaining_seconds: remaining,
    seconds_until_warning: isDriving && status === 'ok' ? Math.max(0, remaining - H) : null,
    seconds_until_limit: isDriving && status !== 'reached' ? remaining : null,
    below_limit_again_at: belowAgainInSeconds == null ? null : iso(nowMs + belowAgainInSeconds * 1000),
  };
};

const week = (nowMs: number, driven: number) => ({
  driven_seconds: driven,
  limit_seconds: 60 * H,
  week_start: '2026-10-26T00:00:00+00:00',
  week_end: '2026-11-02T00:00:00+00:00',
  calculated_at: iso(nowMs),
});

/**
 * 4-month average over a rolling 17-week window, this week included.
 * `allowed` is the total driving this week that keeps the average at 48 h:
 * 48 × 17 − (avg × 17 − this week's driving).
 */
const fourMonth = (nowMs: number, avgHours: number, weekDriven: number, full = true) => {
  const weeks = 17;
  const otherWeeks = avgHours * H * weeks - weekDriven;
  return {
    average_weekly_seconds: Math.round(avgHours * H),
    limit_weekly_seconds: 48 * H,
    window_weeks: weeks,
    allowed_this_week_seconds: Math.round(Math.max(0, 48 * H * weeks - otherWeeks)),
    window_start: '2026-07-06T00:00:00+00:00',
    window_end: '2026-11-02T00:00:00+00:00',
    has_full_history: full,
    calculated_at: iso(nowMs),
  };
};

export const mockLimits = (scenario: LimitsScenario, isDriving: boolean, nowMs = Date.now()): DriverLimits => {
  switch (scenario) {
    case 'ok':
      // Consistent with each other: 3 h 18 in the last 24 h (the current
      // 47 min drive included), 31 h since Monday, 36 h average per week.
      return {
        rolling24h: rolling(nowMs, 3 * H + 18 * M, 'ok', isDriving),
        week: week(nowMs, 31 * H),
        fourMonth: fourMonth(nowMs, 36, 31 * H),
      };
    case 'warning':
      return {
        rolling24h: rolling(nowMs, 6 * H + 5 * M, 'ok', isDriving),
        week: week(nowMs, 49 * H + 30 * M),
        fourMonth: fourMonth(nowMs, 40.2, 49 * H + 30 * M),
      };
    case 'critical':
      return {
        // The existing 10 h warning (ALL-41): 1 h before the limit by default.
        rolling24h: rolling(nowMs, 9 * H + 10 * M, 'warning', isDriving),
        week: week(nowMs, 57 * H + 30 * M),
        fourMonth: fourMonth(nowMs, 46.1, 57 * H + 30 * M),
      };
    case 'reached':
      return {
        rolling24h: rolling(nowMs, 10 * H, 'reached', isDriving, 2 * H + 15 * M),
        week: week(nowMs, 60 * H + 20 * M),
        fourMonth: fourMonth(nowMs, 49.3, 60 * H + 20 * M),
      };
    case 'empty':
    default:
      return { rolling24h: null, week: null, fourMonth: null };
  }
};

export const mockSnapshot = (state: DriverWorkState, nowMs = Date.now()): DriverWorkStateSnapshot => {
  const ago: Record<DriverWorkState, number> = {
    resting: 11 * H + 42 * M,
    available: 12 * M + 30,
    driving: 47 * M + 12,
  };
  return { state, since: iso(nowMs - ago[state] * 1000) };
};
